import React, { useRef, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import PublicPageFooter from '../components/PublicPageFooter';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ROUTES } from '../config/constants';

interface FormData {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  recaptcha?: string;
  submit?: string;
}

// When REACT_APP_RECAPTCHA_SITE_KEY is missing (local dev without the
// frontend env file) OR NODE_ENV is development, the widget is skipped
// and we send an empty token. The backend has the matching server-side
// bypass: if RECAPTCHA_SECRET_KEY is unset it lets the request through
// with a warning. Production must always set both keys.
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_BYPASS =
  !RECAPTCHA_SITE_KEY || process.env.NODE_ENV === 'development';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(
    RECAPTCHA_BYPASS ? 'dev-bypass' : null,
  );

  const from = location.state?.from?.pathname || ROUTES.DASHBOARD;

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email) {
      newErrors.email = t('validation.required', { field: t('auth.email') });
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('validation.email');
    }

    if (!formData.password) {
      newErrors.password = t('validation.required', { field: t('auth.password') });
    } else if (formData.password.length < 6) {
      newErrors.password = t('validation.minLength', { field: t('auth.password'), length: 6 });
    }

    if (!recaptchaToken && !RECAPTCHA_BYPASS) {
      newErrors.recaptcha = t('auth.recaptchaRequired', {
        defaultValue: 'Silakan verifikasi bahwa Anda bukan robot',
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaToken(value);
    if (value && errors.recaptcha) {
      setErrors(prev => ({ ...prev, recaptcha: undefined }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      await login(
        formData.email,
        formData.password,
        recaptchaToken ?? undefined,
      );
      navigate(from, { replace: true });
    } catch (error) {
      setErrors({ submit: t('auth.invalidCredentials') });
      // Reset the widget so the user can retry without a stale token,
      // which Google rejects on the second siteverify call.
      if (!RECAPTCHA_BYPASS) {
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <img
              src="/Mowilex-Corporate.png"
              alt="Mowilex Logo"
              style={{
                maxWidth: '200px',
                height: 'auto',
                marginBottom: '16px'
              }}
            />
          </Box>
          <Typography component="h1" variant="h4" sx={{ mb: 2 }}>
            {t('auth.loginTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('auth.loginSubtitle')}
          </Typography>
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            {errors.submit && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.submit}
              </Alert>
            )}
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('auth.email')}
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
              autoComplete="email"
              autoFocus
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('auth.password')}
              type="password"
              id="password"
              value={formData.password}
              onChange={handleInputChange}
              error={!!errors.password}
              helperText={errors.password}
              autoComplete="current-password"
              disabled={isLoading}
            />

            {!RECAPTCHA_BYPASS && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY!}
                  onChange={handleRecaptchaChange}
                />
              </Box>
            )}
            {errors.recaptcha && (
              <Typography color="error" variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                {errors.recaptcha}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading || (!RECAPTCHA_BYPASS && !recaptchaToken)}
              sx={{ mt: 3, mb: 2 }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('auth.loginButton')
              )}
            </Button>
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link href="/forgot-password" variant="body2">
                {t('auth.forgotPassword')}
              </Link>
            </Box>
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2">
                {t('auth.noAccount')}{' '}
                <Link href={ROUTES.REGISTER} variant="body2">
                  {t('auth.registerHere')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
      <PublicPageFooter />
    </Container>
  );
};

export default LoginPage;